/**
 * KW Command compliance checklists, verbatim from the Wichita market center.
 *
 * R-6 (Daniel 2026-08-04).
 *
 * ═══ Why this exists ══════════════════════════════════════════════════
 *
 * Every transaction document already lives in TR. Somebody then re-uploads
 * each one into KW Command's compliance checklist, one slot at a time,
 * picking the right slot out of a list that runs to seventy rows. Command
 * has no document-upload API available to this team, and its only file
 * sources are "My Computer (Manual)" and "Custom Folders", so nothing can
 * be pushed. The manual step is not going away.
 *
 * What CAN go away is the thinking. If the exported file is already named
 * exactly what the Command slot is called, the job stops being "which of
 * these seventy rows is this" and becomes "type the filename you are
 * looking at". That is the whole idea, and it is why the labels below are
 * captured character for character rather than tidied up. An apostrophe
 * in "Seller's Net Estimate" or the slash in "Tax Sheet/Specials
 * Disclosure" is not a typo to be cleaned; it is the string somebody is
 * going to match against on screen.
 *
 * ═══ Why it lives in shared-types ═════════════════════════════════════
 *
 * The API builds the zip and the merged PDF from this table, and the web
 * app draws the per-slot progress card from it. Two copies of a seventy
 * row compliance list is two compliance lists, and the one that drifts is
 * always the one nobody is looking at. apps/api/src/lib/document-checklists.ts
 * is a hand-kept mirror for exactly this reason and its header explains
 * that a cross-package import was impossible at the time. It is possible
 * now: both apps already import @transaction-rocket/shared-types, so this
 * round takes the shared route and both apps/api/src/lib/command-checklists.ts
 * and apps/web/src/lib/command-checklists.ts are thin re-exports that exist
 * so each app still imports from its own lib directory.
 *
 * ═══ Provenance and one honest gap ════════════════════════════════════
 *
 * Captured live from Command on 2026-08-04. Command's own phase headers
 * reported slot counts that do not match the rows actually listed on
 * screen: Listed said 26 and showed 23, listing Under Contract said 39 and
 * showed 35, buyer Under Contract said 43 and showed 39. The enumerated
 * rows are what was read, so the enumerated rows are what is here. The
 * headers may be counting the free-form "Additional Documents" affordance,
 * or rows that only appear once a prior one is filled. Worth re-checking
 * before anyone treats a phase total as authoritative.
 *
 * The `requirement` value on the last four Closed rows (Invoices, Signed
 * Termite Report, Possession Prior to Closing Agreement, Possession After
 * Closing Addendum) was NOT directly verified on screen and is a best
 * guess. "MLS - Sold" and "Settlement Statement(s)" are confirmed
 * required. Nothing gates on these values, they only sort rows and decide
 * whether a blank slot prints as MISSING, so a wrong guess here costs a
 * line of noise and not a compliance failure.
 */

/* ═════════════════════════════════════════════════════════════════════
 * Shapes
 * ═══════════════════════════════════════════════════════════════════ */

/** Command has two checklists. TR's auction deals use the listing one. */
export type CommandChecklistKey = 'listing' | 'buyer';

/** Command's own Requirement column. */
export type CommandRequirement = 'required' | 'conditional' | 'optional';

/**
 * Command's "Type" column (Agreement / Disclosure / Addendum / ...). Kept
 * as a plain string rather than a union: it is display metadata copied off
 * their screen, Command can add a category without telling anybody, and a
 * union would turn that into a build failure over a word in a table cell.
 */
export type CommandSlotType = string;

export interface CommandSlot {
  /** The label EXACTLY as Command renders it. Never reformat this. */
  slot: string;
  type: CommandSlotType;
  requirement: CommandRequirement;
  /** Set where the captured requirement value is a guess. See header. */
  requirementUnverified?: boolean;
}

export interface CommandPhase {
  /** URL-safe id, used by the merged-PDF endpoint's ?phase= parameter. */
  slug: string;
  /** The real Command phase label. Becomes the zip folder name. */
  label: string;
  slots: CommandSlot[];
}

export interface CommandChecklist {
  key: CommandChecklistKey;
  label: string;
  /** Command's internal checklistType id, captured live. Provenance only. */
  checklistTypeId: number;
  phases: CommandPhase[];
}

/* ═════════════════════════════════════════════════════════════════════
 * CLOSED phase. Byte for byte the same on both checklists, so it is one
 * constant rather than two lists that could drift apart.
 * ═══════════════════════════════════════════════════════════════════ */

const CLOSED_PHASE: CommandPhase = {
  slug: 'closed',
  label: 'Closed',
  slots: [
    { slot: 'Settlement Statement(s)',                 type: 'Agreement',     requirement: 'required' },
    // Yes, both. Command really lists a singular "Settlement Statement" as a
    // separate optional Other row alongside the required Agreement row above.
    // Not a duplicate to be merged.
    { slot: 'Settlement Statement',                    type: 'Other',         requirement: 'optional' },
    { slot: 'MLS - Sold',                              type: 'Other',         requirement: 'required' },
    { slot: 'Final Walk Through',                      type: 'Agreement',     requirement: 'optional' },
    { slot: 'Warranty Deed',                           type: 'Other',         requirement: 'optional' },
    { slot: 'Authorization to Sign',                   type: 'Authorization', requirement: 'optional' },
    { slot: 'Power of Attorney',                       type: 'Other',         requirement: 'optional' },
    { slot: 'Closing Extension',                       type: 'Contract',      requirement: 'conditional' },
    { slot: 'Invoices',                                type: 'Other',         requirement: 'optional', requirementUnverified: true },
    { slot: 'Signed Termite Report',                   type: 'Inspection',    requirement: 'optional', requirementUnverified: true },
    { slot: 'Possession Prior to Closing Agreement',   type: 'Agreement',     requirement: 'optional', requirementUnverified: true },
    { slot: 'Possession After Closing Addendum',       type: 'Addendum',      requirement: 'optional', requirementUnverified: true },
  ],
};

/* ═════════════════════════════════════════════════════════════════════
 * LISTING checklist (Command checklistType 16945).
 * ═══════════════════════════════════════════════════════════════════ */

export const COMMAND_LISTING_CHECKLIST: CommandChecklist = {
  key: 'listing',
  label: 'Listing',
  checklistTypeId: 16945,
  phases: [
    {
      slug: 'listed',
      label: 'Listed',
      slots: [
        { slot: 'Listing Agreement',                      type: 'Agreement',     requirement: 'required' },
        { slot: 'BRRETA',                                 type: 'Disclosure',    requirement: 'optional' },
        { slot: "Seller's Net Estimate",                  type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Lead Paint Brochure',                    type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Lead-Based Paint Disclosure',            type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'Tax Sheet/Specials Disclosure',          type: 'Disclosure',    requirement: 'required' },
        { slot: "Seller's Property Disclosure",           type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Groundwater Addendum',                   type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Radon Disclosure',                       type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Utilities Information',                  type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Mediation',                              type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Wire Fraud Advisory',                    type: 'Disclosure',    requirement: 'optional' },
        { slot: 'MLS Sheet',                              type: 'Other',         requirement: 'required' },
        { slot: 'Energy Efficiency Disclosure',           type: 'Disclosure',    requirement: 'conditional' },
        { slot: "Contractor's Right to Repair Addendum",  type: 'Addendum',      requirement: 'conditional' },
        { slot: 'Coming Soon Waiver',                     type: 'Authorization', requirement: 'conditional' },
        { slot: 'Listing Extension',                      type: 'Agreement',     requirement: 'conditional' },
        { slot: 'Price Change Agreement',                 type: 'Amendment',     requirement: 'conditional' },
        { slot: 'Mortgage Payoff Authorization',          type: 'Authorization', requirement: 'optional' },
        { slot: 'Listing Withdrawal Addendum',            type: 'Addendum',      requirement: 'conditional' },
        { slot: 'Listing Agreement Termination',          type: 'Agreement',     requirement: 'conditional' },
        { slot: 'Cancelled MLS Sheet',                    type: 'Other',         requirement: 'conditional' },
        { slot: 'Compliance Checklist',                   type: 'Other',         requirement: 'optional' },
      ],
    },
    {
      slug: 'under_contract',
      label: 'Under Contract',
      slots: [
        { slot: 'Contract',                               type: 'Contract',      requirement: 'required' },
        { slot: "Seller's Net Estimate",                  type: 'Disclosure',    requirement: 'required' },
        { slot: 'Lead-Based Paint Disclosure',            type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'Tax Sheet/Specials Disclosure',          type: 'Disclosure',    requirement: 'required' },
        { slot: "Seller's Property Disclosure",           type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'Groundwater Addendum',                   type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Statutory Disclosures Addendum',         type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'MLS Sheet',                              type: 'Other',         requirement: 'conditional' },
        { slot: 'Pre-Approval or Proof of Funds Letter',  type: 'Lender',        requirement: 'optional' },
        // Type really is Inspection on the listing side and Addendum on the
        // buyer side for the same label. Captured as seen, not reconciled.
        { slot: 'Well/Septic/Lagoon Addendum',            type: 'Inspection',    requirement: 'optional' },
        { slot: 'Energy Efficiency Disclosure',           type: 'Disclosure',    requirement: 'conditional' },
        { slot: "Contractor's Right to Repair Addendum",  type: 'Addendum',      requirement: 'conditional' },
        { slot: 'ABA Disclosure',                         type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'ABA Disclosure - Agent Owners',          type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'Bill of Sale',                           type: 'Agreement',     requirement: 'optional' },
        { slot: 'Contingency Addendum',                   type: 'Addendum',      requirement: 'conditional' },
        { slot: 'Contingency Removal',                    type: 'Agreement',     requirement: 'conditional' },
        { slot: 'Buyer Agency Agreement',                 type: 'Agreement',     requirement: 'conditional' },
        { slot: 'Transaction Broker Addendum',            type: 'Addendum',      requirement: 'conditional' },
        { slot: 'Buyer Cost Estimate',                    type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'Order Confirmation',                     type: 'Other',         requirement: 'optional' },
        { slot: 'Earnest Money & Contract Receipt',       type: 'Other',         requirement: 'required' },
        { slot: 'Title Commitment',                       type: 'Other',         requirement: 'optional' },
        { slot: 'Repair Request/Inspection Amendment',    type: 'Inspection',    requirement: 'optional' },
        { slot: 'Home Inspection Report',                 type: 'Inspection',    requirement: 'conditional' },
        { slot: 'Termite/Pest Inspection Report',         type: 'Inspection',    requirement: 'optional' },
        { slot: 'Contract Amendment',                     type: 'Amendment',     requirement: 'optional' },
        { slot: 'Contract Addendum',                      type: 'Addendum',      requirement: 'optional' },
        { slot: 'Home Warranty Invoice',                  type: 'Other',         requirement: 'optional' },
        { slot: 'Closing Notice',                         type: 'Other',         requirement: 'optional' },
        { slot: 'Referral Agreement',                     type: 'Agreement',     requirement: 'conditional' },
        { slot: 'Referral Brokerage W-9',                 type: 'Other',         requirement: 'conditional' },
        { slot: 'In Its Present Condition Addendum',      type: 'Addendum',      requirement: 'optional' },
        { slot: 'Purchase Price Escalation Clause Addendum', type: 'Addendum',   requirement: 'optional' },
        { slot: 'Contract Cancellation & Earnest Money Release', type: 'Agreement', requirement: 'conditional' },
      ],
    },
    CLOSED_PHASE,
  ],
};

/* ═════════════════════════════════════════════════════════════════════
 * BUYER checklist (Command checklistType 16946).
 * ═══════════════════════════════════════════════════════════════════ */

export const COMMAND_BUYER_CHECKLIST: CommandChecklist = {
  key: 'buyer',
  label: 'Buyer',
  checklistTypeId: 16946,
  phases: [
    {
      slug: 'consultation',
      label: 'Consultation',
      slots: [
        { slot: 'Buyer Agency Agreement',                 type: 'Agreement',     requirement: 'required' },
        { slot: 'BRRETA',                                 type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Buyer Showing Authorization',            type: 'Authorization', requirement: 'optional' },
        { slot: 'Compliance Checklist',                   type: 'Other',         requirement: 'optional' },
      ],
    },
    {
      slug: 'under_contract',
      label: 'Under Contract',
      slots: [
        { slot: 'Contract',                               type: 'Contract',      requirement: 'required' },
        { slot: 'Buyer Cost Estimate',                    type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Lead Paint Brochure',                    type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Lead-Based Paint Disclosure',            type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'Tax Sheet/Specials Disclosure',          type: 'Disclosure',    requirement: 'required' },
        { slot: "Seller's Property Disclosure",           type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'Groundwater Addendum',                   type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Statutory Disclosures Addendum',         type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'MLS Sheet',                              type: 'Other',         requirement: 'conditional' },
        { slot: 'Mediation',                              type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Wire Fraud Advisory',                    type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Pre-Approval or Proof of Funds Letter',  type: 'Lender',        requirement: 'optional' },
        { slot: 'Radon Disclosure',                       type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Well/Septic/Lagoon Addendum',            type: 'Addendum',      requirement: 'optional' },
        { slot: 'Energy Efficiency Disclosure',           type: 'Disclosure',    requirement: 'conditional' },
        { slot: "Contractor's Right to Repair Addendum",  type: 'Addendum',      requirement: 'conditional' },
        { slot: 'ABA Disclosure',                         type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'ABA Disclosure - Agent Owners',          type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'Bill of Sale',                           type: 'Agreement',     requirement: 'optional' },
        // Contingency Addendum is typed Agreement here and Addendum on the
        // listing side. Captured as seen.
        { slot: 'Contingency Addendum',                   type: 'Agreement',     requirement: 'conditional' },
        { slot: 'Contingency Removal',                    type: 'Agreement',     requirement: 'conditional' },
        { slot: 'Transaction Broker Addendum',            type: 'Addendum',      requirement: 'conditional' },
        { slot: 'For Sale by Owner (FSBO) Authorization', type: 'Authorization', requirement: 'conditional' },
        { slot: "Seller's Net Estimate",                  type: 'Disclosure',    requirement: 'conditional' },
        { slot: 'Order Confirmation',                     type: 'Other',         requirement: 'optional' },
        { slot: 'Earnest Money & Contract Receipt',       type: 'Other',         requirement: 'required' },
        { slot: 'Title Commitment',                       type: 'Other',         requirement: 'optional' },
        { slot: 'Repair Request/Inspection Amendment',    type: 'Inspection',    requirement: 'optional' },
        { slot: 'Home Inspection Report',                 type: 'Inspection',    requirement: 'conditional' },
        { slot: 'Termite/Pest Inspection Report',         type: 'Inspection',    requirement: 'optional' },
        { slot: 'Contract Amendment',                     type: 'Amendment',     requirement: 'optional' },
        { slot: 'Contract Addendum',                      type: 'Addendum',      requirement: 'optional' },
        { slot: 'Home Warranty Invoice',                  type: 'Other',         requirement: 'optional' },
        // Typed Disclosure on the buyer side and Other on the listing side.
        { slot: 'Closing Notice',                         type: 'Disclosure',    requirement: 'optional' },
        { slot: 'Referral Agreement',                     type: 'Agreement',     requirement: 'conditional' },
        { slot: 'Referral Brokerage W-9',                 type: 'Other',         requirement: 'conditional' },
        { slot: 'In Its Present Condition Addendum',      type: 'Addendum',      requirement: 'optional' },
        { slot: 'Purchase Price Escalation Clause Addendum', type: 'Addendum',   requirement: 'optional' },
        { slot: 'Contract Cancellation & Earnest Money Release', type: 'Agreement', requirement: 'conditional' },
      ],
    },
    CLOSED_PHASE,
  ],
};

/** TR deal types. Mirrors ChecklistTxType in the document-checklists files. */
export type CommandTxType = 'buyer' | 'listing' | 'auction';

/**
 * Which Command checklist a TR deal maps to.
 *
 * An auction IS a listing as far as Command is concerned: TurnKey holds the
 * listing side, the auction is a marketing method, and the market center's
 * compliance list has no auction variant.
 */
export function commandChecklistFor(type: CommandTxType): CommandChecklist {
  return type === 'buyer' ? COMMAND_BUYER_CHECKLIST : COMMAND_LISTING_CHECKLIST;
}

/** The folder name for unmapped files, and the phrase the UI shows. */
export const ADDITIONAL_DOCUMENTS_FOLDER = 'Additional Documents';
export const NO_SLOT_REASON = 'no Command slot, goes to Additional Documents';

/* ═════════════════════════════════════════════════════════════════════
 * TR document_type to Command slot, in ONE auditable table.
 *
 * Read this as: "a TR document tagged X should be uploaded into these
 * Command rows". A doc_type with an empty target list is a deliberate
 * decision that Command has no slot for it, not an oversight, and it lands
 * in Additional Documents where Command's free-form rows can take it.
 *
 * ─── Why targets carry a checklist AND a phase ────────────────────────
 *
 * The same document sits in different places on the two checklists.
 * BRRETA is a Listed row for a seller and a Consultation row for a buyer.
 * A buyer agency agreement is Consultation on the buyer checklist and
 * Under Contract on the listing one, because on a listing it is the OTHER
 * side's agreement arriving with the offer. Wire Fraud Advisory and
 * Mediation are Listed on a listing and Under Contract on a buyer file.
 * None of that can be expressed by phase alone.
 *
 * ─── Why some documents target more than one row ──────────────────────
 *
 * Command genuinely lists the same slot in two phases and wants the file
 * in both. Lead-Based Paint Disclosure, Seller's Property Disclosure,
 * Groundwater Addendum, Tax Sheet/Specials Disclosure and Seller's Net
 * Estimate all appear at Listed and again at Under Contract. The packet
 * therefore writes the same PDF into both phase folders. Copying a file is
 * cheap; making somebody find it twice is not.
 *
 * ─── TR doc_type values are the REAL ones ─────────────────────────────
 *
 * Taken from CONTRACT_DOC_TYPE_OPTIONS in apps/web/src/api/documents.ts,
 * not from memory. Several plausible-sounding names are not what TR
 * actually stores: it is `tax_form` not signed_tax_form, `wire_fraud` not
 * wire_fraud_form, `mediation` not mediation_form, `em_receipt` not
 * earnest_money_receipt, `pre_approval` not pre_approval_pre_qual,
 * `lead_based_paint` not lead_based_paint_disclosure,
 * `well_septic_inspection` not well_septic_lagoon_inspection, and
 * `mls_sheet` not mls_sheet_at_contract. There is no
 * `auction_listing_agreement`: an auction listing is stored as
 * `listing_agreement`, and since auctions use the Command listing
 * checklist that row already covers it.
 *
 * The wrong-but-plausible spellings are kept as aliases anyway. They cost
 * one line each and they mean a hand-rolled upload or a future rename
 * cannot quietly drop a signed contract into Additional Documents.
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Separator for the composite Map keys below (checklist, phase, slug, label).
 *
 * A unit separator and not a space, a colon or a pipe: every Command slot
 * label is arbitrary text off somebody else's screen and any printable
 * separator is a character a future label could legitimately contain, which
 * would collide two different slots onto one key. Nothing readable is lost
 * because these keys are never displayed.
 */
const KEY_SEP = '\u001F';

export interface CommandSlotTarget {
  checklist: CommandChecklistKey;
  /** CommandPhase.slug */
  phase: string;
  /** Must match a CommandSlot.slot on that phase exactly. Asserted below. */
  slot: string;
}

export interface CommandMappingRow {
  doc_type: string;
  targets: CommandSlotTarget[];
  /** Shown in the manifest and in the UI when a call was a judgement. */
  note?: string;
}

/** Shorthand so the table below reads as data rather than as object soup. */
function L(phase: string, slot: string): CommandSlotTarget {
  return { checklist: 'listing', phase, slot };
}
function B(phase: string, slot: string): CommandSlotTarget {
  return { checklist: 'buyer', phase, slot };
}

export const COMMAND_DOC_TYPE_MAP: CommandMappingRow[] = [
  // ── Core contracts ────────────────────────────────────────────────
  { doc_type: 'listing_agreement', targets: [L('listed', 'Listing Agreement')],
    note: 'Auction listings use this same type; auctions run on the Command listing checklist.' },
  { doc_type: 'auction_listing_agreement', targets: [L('listed', 'Listing Agreement')],
    note: 'Alias. Not a real TR doc_type today, kept so a future rename cannot lose the agreement.' },
  { doc_type: 'purchase_agreement', targets: [L('under_contract', 'Contract'), B('under_contract', 'Contract')] },
  { doc_type: 'auction_purchase_agreement', targets: [L('under_contract', 'Contract'), B('under_contract', 'Contract')] },
  { doc_type: 'buyer_agency_agreement',
    targets: [B('consultation', 'Buyer Agency Agreement'), L('under_contract', 'Buyer Agency Agreement')],
    note: "On a listing this is the buyer side's agreement arriving with the offer, which is why it is Under Contract there." },
  { doc_type: 'buyer_showing_authorization', targets: [B('consultation', 'Buyer Showing Authorization')],
    note: 'The Command listing checklist has no showing-authorization row, so on a listing or auction deal this goes to Additional Documents.' },
  { doc_type: 'fsbo_authorization', targets: [B('under_contract', 'For Sale by Owner (FSBO) Authorization')],
    note: 'Buyer checklist only. Command has no FSBO row on the listing side.' },

  // ── Agency / disclosures ──────────────────────────────────────────
  { doc_type: 'agency_disclosure_brreta', targets: [L('listed', 'BRRETA'), B('consultation', 'BRRETA')] },
  { doc_type: 'brreta', targets: [L('listed', 'BRRETA'), B('consultation', 'BRRETA')], note: 'Alias.' },
  { doc_type: 'lead_based_paint',
    targets: [
      L('listed', 'Lead-Based Paint Disclosure'),
      L('under_contract', 'Lead-Based Paint Disclosure'),
      B('under_contract', 'Lead-Based Paint Disclosure'),
    ] },
  { doc_type: 'lead_based_paint_disclosure',
    targets: [
      L('listed', 'Lead-Based Paint Disclosure'),
      L('under_contract', 'Lead-Based Paint Disclosure'),
      B('under_contract', 'Lead-Based Paint Disclosure'),
    ], note: 'Alias.' },
  { doc_type: 'sellers_property_disclosure',
    targets: [
      L('listed', "Seller's Property Disclosure"),
      L('under_contract', "Seller's Property Disclosure"),
      B('under_contract', "Seller's Property Disclosure"),
    ] },
  { doc_type: 'groundwater_addendum',
    targets: [
      L('listed', 'Groundwater Addendum'),
      L('under_contract', 'Groundwater Addendum'),
      B('under_contract', 'Groundwater Addendum'),
    ] },
  { doc_type: 'tax_sheet',
    targets: [
      L('listed', 'Tax Sheet/Specials Disclosure'),
      L('under_contract', 'Tax Sheet/Specials Disclosure'),
      B('under_contract', 'Tax Sheet/Specials Disclosure'),
    ] },
  { doc_type: 'tax_form',
    targets: [
      L('listed', 'Tax Sheet/Specials Disclosure'),
      L('under_contract', 'Tax Sheet/Specials Disclosure'),
      B('under_contract', 'Tax Sheet/Specials Disclosure'),
    ],
    note: 'JUDGEMENT CALL. TR treats tax_form ("Signed tax form") as a Closed phase document; Command has no tax row in Closed and only carries Tax Sheet/Specials Disclosure at Listed and Under Contract. Filed there. Verify with Rachelle whether the signed tax form belongs in Command at all.' },
  { doc_type: 'signed_tax_form',
    targets: [
      L('listed', 'Tax Sheet/Specials Disclosure'),
      L('under_contract', 'Tax Sheet/Specials Disclosure'),
      B('under_contract', 'Tax Sheet/Specials Disclosure'),
    ], note: 'Alias.' },
  { doc_type: 'wire_fraud', targets: [L('listed', 'Wire Fraud Advisory'), B('under_contract', 'Wire Fraud Advisory')] },
  { doc_type: 'wire_fraud_form', targets: [L('listed', 'Wire Fraud Advisory'), B('under_contract', 'Wire Fraud Advisory')], note: 'Alias.' },
  { doc_type: 'mediation', targets: [L('listed', 'Mediation'), B('under_contract', 'Mediation')] },
  { doc_type: 'mediation_form', targets: [L('listed', 'Mediation'), B('under_contract', 'Mediation')], note: 'Alias.' },
  { doc_type: 'aba_form', targets: [L('under_contract', 'ABA Disclosure'), B('under_contract', 'ABA Disclosure')],
    note: 'The plain ABA Disclosure row, not "ABA Disclosure - Agent Owners", which is the narrower agent-owned-property form TR does not model.' },

  // ── Money / estimates ─────────────────────────────────────────────
  { doc_type: 'buyer_cost_estimate', targets: [L('under_contract', 'Buyer Cost Estimate'), B('under_contract', 'Buyer Cost Estimate')] },
  { doc_type: 'buyer_estimated_closing_costs', targets: [L('under_contract', 'Buyer Cost Estimate'), B('under_contract', 'Buyer Cost Estimate')] },
  { doc_type: 'sellers_net_estimate',
    targets: [
      L('listed', "Seller's Net Estimate"),
      L('under_contract', "Seller's Net Estimate"),
      B('under_contract', "Seller's Net Estimate"),
    ] },
  { doc_type: 'updated_seller_net',
    targets: [
      L('listed', "Seller's Net Estimate"),
      L('under_contract', "Seller's Net Estimate"),
      B('under_contract', "Seller's Net Estimate"),
    ],
    note: 'The updated net and the original net share one Command row. When a deal has both, the packet suffixes the second copy rather than dropping it.' },
  { doc_type: 'em_receipt', targets: [L('under_contract', 'Earnest Money & Contract Receipt'), B('under_contract', 'Earnest Money & Contract Receipt')] },
  { doc_type: 'earnest_money_receipt', targets: [L('under_contract', 'Earnest Money & Contract Receipt'), B('under_contract', 'Earnest Money & Contract Receipt')], note: 'Alias.' },
  { doc_type: 'closing_disclosure', targets: [L('closed', 'Settlement Statement(s)'), B('closed', 'Settlement Statement(s)')] },
  { doc_type: 'pre_approval', targets: [L('under_contract', 'Pre-Approval or Proof of Funds Letter'), B('under_contract', 'Pre-Approval or Proof of Funds Letter')] },
  { doc_type: 'pre_approval_pre_qual', targets: [L('under_contract', 'Pre-Approval or Proof of Funds Letter'), B('under_contract', 'Pre-Approval or Proof of Funds Letter')], note: 'Alias.' },
  { doc_type: 'proof_of_funds', targets: [L('under_contract', 'Pre-Approval or Proof of Funds Letter'), B('under_contract', 'Pre-Approval or Proof of Funds Letter')] },
  { doc_type: 'prequal', targets: [L('under_contract', 'Pre-Approval or Proof of Funds Letter'), B('under_contract', 'Pre-Approval or Proof of Funds Letter')],
    note: 'Legacy type, but TR already treats it as a pre-approval via alsoAccepts and Command has an exact row for it. Filing a real pre-qual under Additional Documents would be a worse answer than mapping a legacy name.' },

  // ── Loan file. Command has no rows for these. ─────────────────────
  { doc_type: 'appraisal', targets: [], note: NO_SLOT_REASON + '. Command carries no appraisal row on either checklist.' },
  { doc_type: 'loan_commitment', targets: [], note: NO_SLOT_REASON + '. The Lender-typed row is the pre-approval / proof of funds letter, which a commitment is not.' },
  { doc_type: 'lender_docs', targets: [], note: NO_SLOT_REASON + '. Too broad to file into one row.' },

  // ── Modifications ─────────────────────────────────────────────────
  { doc_type: 'amendment', targets: [L('under_contract', 'Contract Amendment'), B('under_contract', 'Contract Amendment')] },
  { doc_type: 'addendum', targets: [L('under_contract', 'Contract Addendum'), B('under_contract', 'Contract Addendum')] },
  { doc_type: 'repair_amendment', targets: [L('under_contract', 'Repair Request/Inspection Amendment'), B('under_contract', 'Repair Request/Inspection Amendment')] },

  // ── Inspections ───────────────────────────────────────────────────
  { doc_type: 'inspection_report', targets: [L('under_contract', 'Home Inspection Report'), B('under_contract', 'Home Inspection Report')] },
  { doc_type: 'termite_inspection', targets: [L('under_contract', 'Termite/Pest Inspection Report'), B('under_contract', 'Termite/Pest Inspection Report')],
    note: 'The Under Contract report row. Command also has "Signed Termite Report" in Closed; that is the signed-off copy at closing and TR does not model it separately.' },
  { doc_type: 'sewer_scope_inspection', targets: [], note: NO_SLOT_REASON + '. Command has no sewer scope row.' },
  { doc_type: 'well_septic_inspection', targets: [L('under_contract', 'Well/Septic/Lagoon Addendum'), B('under_contract', 'Well/Septic/Lagoon Addendum')],
    note: 'JUDGEMENT CALL. Command names this row an Addendum and TR stores an inspection. It is the only well/septic/lagoon row Command has, so the report goes there rather than into Additional Documents.' },

  // ── Other transaction documents ───────────────────────────────────
  { doc_type: 'home_warranty', targets: [L('under_contract', 'Home Warranty Invoice'), B('under_contract', 'Home Warranty Invoice')] },
  { doc_type: 'title_commitment', targets: [L('under_contract', 'Title Commitment'), B('under_contract', 'Title Commitment')] },
  // Build 54 (Angel 2026-09-22): two new TR checklist slots.
  { doc_type: 'prelim_title_commitment', targets: [L('under_contract', 'Title Commitment'), B('under_contract', 'Title Commitment')],
    note: 'JUDGEMENT CALL. Command has one Title Commitment row and no preliminary one, so the prelim files there. When a deal has both, the packet suffixes the second copy rather than dropping it.' },
  { doc_type: 'final_walkthrough', targets: [L('closed', 'Final Walk Through'), B('closed', 'Final Walk Through')] },
  { doc_type: 'mls_sheet', targets: [L('under_contract', 'MLS Sheet'), B('under_contract', 'MLS Sheet')] },
  { doc_type: 'mls_sheet_at_contract', targets: [L('under_contract', 'MLS Sheet'), B('under_contract', 'MLS Sheet')], note: 'Alias.' },
  { doc_type: 'active_mls_printout', targets: [L('listed', 'MLS Sheet'), B('under_contract', 'MLS Sheet')],
    note: 'JUDGEMENT CALL. The active printout is the Listed MLS Sheet on a listing. The buyer checklist has no Consultation MLS row, so on a buyer file it fills the Under Contract MLS Sheet, which is the only MLS row that checklist has.' },

  // ── Legacy and catch-all. Deliberately unmapped. ──────────────────
  { doc_type: 'contract', targets: [],
    note: NO_SLOT_REASON + '. The legacy generic type. It could be a listing agreement or a purchase agreement and guessing wrong files a contract into the wrong phase, so a human decides.' },
  { doc_type: 'disclosure', targets: [], note: NO_SLOT_REASON + '. Legacy generic type, and Command has fourteen disclosure rows.' },
  { doc_type: 'inspection', targets: [], note: NO_SLOT_REASON + '. Legacy generic type.' },
  { doc_type: 'other', targets: [], note: NO_SLOT_REASON + '.' },
];

/**
 * Document types that must NEVER appear in a Command packet, whatever the
 * mapping says.
 *
 * A Disbursement Authorization is the commission structure of the deal in
 * one PDF and it is not a compliance document. See lib/da-visibility.ts.
 * The API route also filters it out in SQL; this is the second layer, so a
 * caller who hands the planner a pre-fetched document list cannot get one
 * into a zip by accident.
 */
export const COMMAND_PACKET_EXCLUDED_DOC_TYPES: readonly string[] = ['disbursement_authorization'];

const MAP_BY_DOC_TYPE = new Map<string, CommandMappingRow>(
  COMMAND_DOC_TYPE_MAP.map((row) => [row.doc_type, row]),
);

/** The mapping row for a TR document_type, or null when nothing matches. */
export function commandMappingFor(docType: string | null | undefined): CommandMappingRow | null {
  if (!docType) return null;
  return MAP_BY_DOC_TYPE.get(docType) ?? null;
}

/* ═════════════════════════════════════════════════════════════════════
 * Filenames
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * A Command slot label turned into something a filesystem will accept.
 *
 * The point of the whole export is that the filename reads like the slot
 * label, so this changes as little as it can get away with. Only the
 * characters Windows and macOS actually reject are touched:
 *
 *   /  and  \   become  -    ("Tax Sheet-Specials Disclosure")
 *   :           becomes -
 *   * ? " < > | are dropped
 *
 * Apostrophes, ampersands, parentheses, hyphens and commas are all legal
 * and stay exactly as Command writes them. Trailing dots and spaces are
 * trimmed because Windows silently drops them, which would leave a name
 * that no longer matches what the manifest promised.
 */
export function sanitizeSlotFileName(slot: string): string {
  return slot
    .replace(/[/\\:]/g, '-')
    .replace(/[*?"<>|]/g, '')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/, '')
    .trim();
}

/**
 * The extension to keep on a packet file.
 *
 * Command's manual upload takes whatever you hand it, and a JPG of a
 * signed page is still the document, so the original extension is
 * preserved rather than forced to .pdf. Falls back to the mime type, then
 * to .pdf, because a name with no extension is the one thing that makes
 * Command's file picker refuse outright.
 */
export function packetExtension(fileName: string | null | undefined, mimeType?: string | null): string {
  const fromName = /\.([A-Za-z0-9]{1,8})$/.exec((fileName ?? '').trim());
  if (fromName) return `.${fromName[1].toLowerCase()}`;
  const mime = (mimeType ?? '').toLowerCase();
  if (mime.includes('pdf')) return '.pdf';
  if (mime.includes('png')) return '.png';
  if (mime.includes('jpeg') || mime.includes('jpg')) return '.jpg';
  if (mime.includes('heic')) return '.heic';
  if (mime.includes('webp')) return '.webp';
  if (mime.includes('wordprocessingml')) return '.docx';
  if (mime.includes('msword')) return '.doc';
  if (mime.includes('spreadsheetml')) return '.xlsx';
  if (mime.includes('ms-excel')) return '.xls';
  if (mime.includes('csv')) return '.csv';
  if (mime.startsWith('text/')) return '.txt';
  return '.pdf';
}

/** A TR filename made safe for the Additional Documents folder. */
export function sanitizeFreeFileName(name: string): string {
  const cleaned = name
    .replace(/[/\\:]/g, '-')
    .replace(/[*?"<>|]/g, '')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/, '')
    .trim();
  return cleaned || 'Untitled';
}

/* ═════════════════════════════════════════════════════════════════════
 * The plan
 * ═══════════════════════════════════════════════════════════════════ */

/** The minimum a caller has to know about a TR document to plan a packet. */
export interface CommandPlanDocument {
  id: number;
  name: string;
  document_type: string | null;
  mime_type?: string | null;
}

export interface CommandPacketRow {
  slot: string;
  type: CommandSlotType;
  requirement: CommandRequirement;
  /** Position of the slot inside its phase, so the UI can restore Command's own order. */
  slot_index: number;
  /** 1 for the first file in a slot, 2 for the second, and so on. */
  instance: number;
  tr_document_id: number | null;
  tr_file_name: string | null;
  /** Null only when nothing fills the slot. */
  packet_file_name: string | null;
  status: 'filled' | 'missing';
  /** Set when sanitizing changed the label, so the manifest can say so. */
  renamed_from?: string;
  /** The mapping note, when the call was a judgement worth reading. */
  note?: string;
  requirementUnverified?: boolean;
}

export interface CommandPacketPhasePlan {
  slug: string;
  label: string;
  rows: CommandPacketRow[];
  /** Distinct slots with at least one file. */
  filled: number;
  /** Distinct slots in the phase. */
  total: number;
  requiredTotal: number;
  requiredFilled: number;
}

export interface CommandPacketGap {
  phase: string;
  phase_slug: string;
  slot: string;
  requirement: 'required' | 'conditional';
}

export interface CommandPacketAdditional {
  tr_document_id: number;
  tr_file_name: string;
  document_type: string | null;
  packet_file_name: string;
  reason: string;
}

export interface CommandPacketPlan {
  checklist_key: CommandChecklistKey;
  checklist_label: string;
  checklist_type_id: number;
  phases: CommandPacketPhasePlan[];
  /** Required and conditional slots with no TR file behind them. */
  gaps: CommandPacketGap[];
  additional: CommandPacketAdditional[];
}

/** required first, then conditional, then optional. */
const REQUIREMENT_RANK: Record<CommandRequirement, number> = {
  required: 0,
  conditional: 1,
  optional: 2,
};

export function commandRequirementRank(r: CommandRequirement): number {
  return REQUIREMENT_RANK[r];
}

/**
 * Work out, for one deal, exactly what goes into which Command row.
 *
 * Pure. No IO, no R2, no clock. That is deliberate: the same function
 * produces the zip layout, the merged PDF's table of contents, the JSON
 * the UI renders and the gap report, and if any two of those disagreed
 * about where a file goes then the manifest would be lying about the zip
 * sitting next to it.
 *
 * Ordering inside a phase follows Command's own row order, not the
 * requirement sort. The zip and the merged PDF want to read down the
 * screen in the same order Rachelle is clicking. The UI sorts by
 * requirement itself, using slot_index to stay stable.
 */
export function planCommandPacket(
  transactionType: CommandTxType,
  documents: CommandPlanDocument[],
): CommandPacketPlan {
  const checklist = commandChecklistFor(transactionType);

  const usable = documents.filter(
    (d) => !COMMAND_PACKET_EXCLUDED_DOC_TYPES.includes(d.document_type ?? ''),
  );

  // Which documents land in which (phase slug, slot) pair on THIS checklist.
  const bySlot = new Map<string, CommandPlanDocument[]>();
  const matched = new Set<number>();
  const noteBySlot = new Map<string, string>();

  for (const doc of usable) {
    const mapping = commandMappingFor(doc.document_type);
    if (!mapping) continue;
    const targets = mapping.targets.filter((t) => t.checklist === checklist.key);
    if (targets.length === 0) continue;
    matched.add(doc.id);
    for (const target of targets) {
      const key = `${target.phase}${KEY_SEP}${target.slot}`;
      const list = bySlot.get(key);
      if (list) list.push(doc);
      else bySlot.set(key, [doc]);
      if (mapping.note && !noteBySlot.has(key)) noteBySlot.set(key, mapping.note);
    }
  }

  const gaps: CommandPacketGap[] = [];

  const phases: CommandPacketPhasePlan[] = checklist.phases.map((phase) => {
    const rows: CommandPacketRow[] = [];
    let filled = 0;
    let requiredTotal = 0;
    let requiredFilled = 0;

    phase.slots.forEach((slot, slotIndex) => {
      const key = `${phase.slug}${KEY_SEP}${slot.slot}`;
      const docs = bySlot.get(key) ?? [];
      const base = sanitizeSlotFileName(slot.slot);
      const renamedFrom = base === slot.slot ? undefined : slot.slot;
      const note = noteBySlot.get(key);

      if (slot.requirement === 'required') requiredTotal += 1;

      if (docs.length === 0) {
        rows.push({
          slot: slot.slot,
          type: slot.type,
          requirement: slot.requirement,
          slot_index: slotIndex,
          instance: 1,
          tr_document_id: null,
          tr_file_name: null,
          packet_file_name: null,
          status: 'missing',
          renamed_from: renamedFrom,
          note,
          requirementUnverified: slot.requirementUnverified,
        });
        if (slot.requirement === 'required' || slot.requirement === 'conditional') {
          gaps.push({
            phase: phase.label,
            phase_slug: phase.slug,
            slot: slot.slot,
            requirement: slot.requirement,
          });
        }
        return;
      }

      filled += 1;
      if (slot.requirement === 'required') requiredFilled += 1;

      docs.forEach((doc, i) => {
        // Two TR files in one Command row keep the slot name and gain a
        // suffix. Command's Additional Documents rows can take the spares,
        // but a second earnest money receipt named after its slot is still
        // more useful than one named "Scan 4.pdf".
        const suffix = i === 0 ? '' : ` (${i + 1})`;
        rows.push({
          slot: slot.slot,
          type: slot.type,
          requirement: slot.requirement,
          slot_index: slotIndex,
          instance: i + 1,
          tr_document_id: doc.id,
          tr_file_name: doc.name,
          packet_file_name: `${base}${suffix}${packetExtension(doc.name, doc.mime_type)}`,
          status: 'filled',
          renamed_from: renamedFrom,
          note,
          requirementUnverified: slot.requirementUnverified,
        });
      });
    });

    return {
      slug: phase.slug,
      label: phase.label,
      rows,
      filled,
      total: phase.slots.length,
      requiredTotal,
      requiredFilled,
    };
  });

  // Everything TR holds that no Command row wants. Never dropped: Command
  // allows free-form Additional Documents rows and that is where these go.
  const usedNames = new Set<string>();
  const additional: CommandPacketAdditional[] = [];
  for (const doc of usable) {
    if (matched.has(doc.id)) continue;
    const mapping = commandMappingFor(doc.document_type);
    let name = sanitizeFreeFileName(doc.name);
    if (usedNames.has(name.toLowerCase())) {
      const dot = name.lastIndexOf('.');
      const stem = dot > 0 ? name.slice(0, dot) : name;
      const ext = dot > 0 ? name.slice(dot) : '';
      let n = 2;
      while (usedNames.has(`${stem} (${n})${ext}`.toLowerCase())) n += 1;
      name = `${stem} (${n})${ext}`;
    }
    usedNames.add(name.toLowerCase());
    additional.push({
      tr_document_id: doc.id,
      tr_file_name: doc.name,
      document_type: doc.document_type,
      packet_file_name: name,
      reason: mapping?.note ?? NO_SLOT_REASON,
    });
  }

  return {
    checklist_key: checklist.key,
    checklist_label: checklist.label,
    checklist_type_id: checklist.checklistTypeId,
    phases,
    gaps,
    additional,
  };
}

/**
 * Every (checklist, phase, slot) the mapping table points at that does not
 * exist on that checklist.
 *
 * A typo in a slot string is the failure mode this whole file is built to
 * avoid: the mapping would silently match nothing, the document would drop
 * into Additional Documents, and the only symptom would be a slot that is
 * permanently empty on every deal. Nothing about that looks like a bug.
 *
 * Called by scripts and by the packet route's dev assertion rather than at
 * module load, so a bad string cannot take the Worker down on boot.
 */
export function findBrokenCommandMappings(): string[] {
  const valid = new Set<string>();
  for (const checklist of [COMMAND_LISTING_CHECKLIST, COMMAND_BUYER_CHECKLIST]) {
    for (const phase of checklist.phases) {
      for (const slot of phase.slots) {
        valid.add(`${checklist.key}${KEY_SEP}${phase.slug}${KEY_SEP}${slot.slot}`);
      }
    }
  }
  const broken: string[] = [];
  for (const row of COMMAND_DOC_TYPE_MAP) {
    for (const t of row.targets) {
      if (!valid.has(`${t.checklist}${KEY_SEP}${t.phase}${KEY_SEP}${t.slot}`)) {
        broken.push(`${row.doc_type} -> ${t.checklist}/${t.phase}/"${t.slot}"`);
      }
    }
  }
  return broken;
}

/*
 * ═══ F-2 (Daniel 2026-08-13): how the MONEY is entered in Command ═════════
 *
 * The Market Center Administrator answered the team's open money questions
 * in writing on 2026-08-13. Three of the answers are about how a HUMAN fills
 * in Command's own screens, which TR cannot reach, so they cannot be encoded
 * in the money engine the way the caps and fees were (see lib/kw-splits.ts
 * and migration 0210 in apps/api). Instructions a person needs at the moment
 * of data entry belong on the surface they are looking at while they do it,
 * which is the Command compliance card. That is what this list is.
 *
 * Wording rules: plain sentences, the WHY included, no exclamation marks,
 * because a checklist instruction that does not say what goes wrong when it
 * is skipped gets skipped.
 */
export interface CommandMoneyGuidanceItem {
  title: string;
  body: string;
}

export const COMMAND_MONEY_ENTRY_GUIDANCE: readonly CommandMoneyGuidanceItem[] = [
  {
    title: 'Offer tab: choose TEAM for every agent, never Single',
    body:
      'The team side of every commission posts to the rainmaker\'s KWUID (Daniel\'s). '
      + 'On the Offer tab each agent on the deal must be entered with TEAM selected. '
      + 'Choosing Single books the whole GCI to that agent\'s own number, which misstates '
      + 'their cap and Daniel\'s. (MCA, 2026-08-13.)',
  },
  {
    title: 'Concessions to close come off the top of GCI',
    body:
      'Money the team gives up to get a deal closed reduces the GCI BEFORE the split math. '
      + 'Enter it by reducing the commission figure, not in Command\'s Concessions field. '
      + 'The Concessions field does not change what company dollar and royalty are charged '
      + 'on, so using it overstates everyone\'s deductions. (MCA, 2026-08-13.)',
  },
  {
    title: 'Showing partner fills are additional agents with their own percentages',
    body:
      'When a showing partner covers part of a deal, add them to the Command deal as an '
      + 'additional agent with their own percentage. Several agents on one deal are fine, '
      + 'for example 55/35/10. Folding their share into the primary agent\'s line puts the '
      + 'wrong GCI on both people\'s caps. (MCA, 2026-08-13.)',
  },
];
